import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import TabbedThreadList from "./TabbedThreadList";
import ThreadMessageList from "./ThreadMessageList";
import NewThreadDialog from "../NewThreadDialog";
import BaseBanner from "../BaseBanner";
import "./MessageCenterLayout.css";

type MessageCenterLayoutProps = {
  currentUserUUID: string;
  replyDisclaimer?: string;
  replyTime: string;
  userMessageUploads?: boolean;
};

type SelectedThreadId = string | undefined;

type ThreadSummary = {
  threadId: string;
  subject: string;
  updatedAt: string;
  isRead: boolean;
};

type LoadThreadsParams = {
  startDate?: string;
  endDate?: string;
};

type LoadThreadsResult = {
  threads: ThreadSummary[];
};

type ThreadApiResponse = {
  threads: {
    id: string;
    subject: string;
    updated_at: string;
    is_read: boolean;
  }[];
};

export default function MessageCenterLayout({
  currentUserUUID,
  replyDisclaimer,
  replyTime,
  userMessageUploads,
}: MessageCenterLayoutProps) {
  const { l10n } = useLocalization();
  const [selectedThreadId, setSelectedThreadId] = useState<SelectedThreadId>(
    () => {
      const [, uuid] = window.location.pathname
        .replace(/^\//, "")
        .replace(/\/$/, "")
        .split("/");
      if (uuid !== "messages") {
        return uuid;
      }
      return undefined;
    }
  );
  const [isDialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const nextPath: SelectedThreadId[] = ["messages"];
    nextPath.push(selectedThreadId);
    window.history.pushState({}, "", `/${nextPath.join("/")}`);
  }, [selectedThreadId]);

  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const loadThreads = async ({
    startDate,
    endDate,
  }: LoadThreadsParams): Promise<LoadThreadsResult> => {
    const params: {
      order_by: string;
      start_date?: string;
      end_date?: string;
    } = { order_by: "updated_at" };

    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const result = (await ApiHttp.fetch("threads", {
      params,
    })) as ThreadApiResponse;

    return {
      threads: result.threads.map((thread) => ({
        threadId: thread.id,
        subject: thread.subject,
        updatedAt: thread.updated_at,
        isRead: thread.is_read,
      })),
    };
  };

  const loadAllThreads = () => {
    const startingThread = loadThreads({
      startDate: threeMonthsAgo.toISOString(),
    });
    const endingThread = loadThreads({ endDate: threeMonthsAgo.toISOString() });
    return { startingThread, endingThread };
  };

  return (
    <>
      <BaseBanner
        bannerName={l10n.getString("banner-message-center")}
        buttonFunction={() => {
          setDialogOpen(true);
        }}
        buttonName={`+ ${l10n.getString("banner-button-add-message")}`}
        features={{ user_message_uploads: true }}
        orFeatureFlagChecks={["user_message_uploads"]}
      ></BaseBanner>
      <NewThreadDialog
        isDialogOpen={isDialogOpen}
        replyTime={replyTime}
        closeDialog={() => setDialogOpen(false)}
        callback={() => {
          loadAllThreads().startingThread.then((result) => {
            if (result.threads.length > 0) {
              setSelectedThreadId(result.threads[0].threadId);
            }
          });
        }}
      />
      <div className="message-center-layout">
        <TabbedThreadList
          selectedThreadId={selectedThreadId}
          setSelectedThreadId={setSelectedThreadId}
          loadAllThreads={loadAllThreads}
        />
        <ThreadMessageList
          threadId={selectedThreadId}
          currentUserUUID={currentUserUUID}
          replyTime={replyTime}
          replyDisclaimer={replyDisclaimer}
          userMessageUploads={userMessageUploads}
        />
      </div>
    </>
  );
}
